// Runtime polyfills for APIs newer than our supported-browser floor.
// Import FIRST in each app's main.tsx (before anything that may use them).

type WithResolvers = <T>() => {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

// Promise.withResolvers — ES2024, missing in Safari < 17.4 / Chrome < 119.
// pdfjs-dist ≥ 4 calls it during module evaluation, which crashed client
// browsers into the route error boundary (seen in prod 2026-07). The pdf
// WORKER runs in its own context and can't see this polyfill — that's why
// the apps ship pdfjs's `legacy` worker build (self-polyfilled), see
// `copyPdfWorkerPlugin` in each vite.config.ts.
const P = Promise as PromiseConstructor & { withResolvers?: WithResolvers };
if (typeof P.withResolvers !== 'function') {
  const withResolvers: WithResolvers = <T,>() => {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
  // defineProperty, not assignment: the native method is non-enumerable, and a
  // plain assign would make it show up in `for…in` / `Object.keys(Promise)`.
  Object.defineProperty(P, 'withResolvers', {
    value: withResolvers,
    writable: true,
    configurable: true,
    enumerable: false,
  });
}

export {};
